/**
 * SVGUtils - Lightweight SVG manipulation library
 * A custom implementation to replace SVG.js with only the necessary functionality
 */
export class SVGCanvas {
    constructor(container) {
        this.container = container;
        this.svgElement = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "svg"
        );
        this.svgElement.setAttribute("width", "100%");
        this.svgElement.setAttribute("height", "100%");
        this.container.appendChild(this.svgElement);
        this.elements = [];
        return this;
    }

    // Set SVG dimensions
    size(width, height) {
        this.svgElement.setAttribute("width", width);
        this.svgElement.setAttribute("height", height);
        return this;
    }

    // Set viewBox
    viewbox(x, y, width, height) {
        this.svgElement.setAttribute("viewBox", `${x} ${y} ${width} ${height}`);
        return this;
    }

    // Clear all elements from SVG
    clear() {
        while (this.svgElement.firstChild) {
            this.svgElement.removeChild(this.svgElement.firstChild);
        }
        this.elements = [];
        return this;
    }

    // Create a polygon
    polygon() {
        const polygon = new SVGPolygon(this.svgElement);
        this.elements.push(polygon);
        return polygon;
    }

    // Create a circle
    circle(diameter) {
        const circle = new SVGCircle(this.svgElement, diameter);
        this.elements.push(circle);
        return circle;
    }

    // Static method to create SVG in container
    static addTo(container) {
        return new SVGCanvas(container);
    }
}

// Base class for SVG elements with common methods
class SVGElement {
    constructor(svgElement, type) {
        this.svgElement = svgElement;
        this.element = document.createElementNS(
            "http://www.w3.org/2000/svg",
            type
        );

        // Вставляем новый элемент в начало SVG, чтобы он был поверх других
        // if (this.svgElement.firstChild) {
        //     this.svgElement.insertBefore(
        //         this.element,
        //         this.svgElement.firstChild
        //     );
        // } else {
        //     this.svgElement.appendChild(this.element);
        // }

        this.svgElement.appendChild(this.element);

        // Event handling support
        this._eventListeners = {};

        // Store current style properties
        this._currentFill = { color: "black", opacity: 1 };
        this._currentStroke = { color: "none", width: 1, opacity: 1 };
    }

    // Move element to front (top of z-order)
    front() {
        if (this.element && this.element.parentNode) {
            // Удаляем элемент из DOM
            const parent = this.element.parentNode;
            parent.removeChild(this.element);

            // Вставляем в начало (в SVG первый элемент находится внизу, последний - наверху)
            if (parent.firstChild) {
                parent.insertBefore(this.element, parent.firstChild);
            } else {
                parent.appendChild(this.element);
            }
        }
        return this;
    }

    // Move element to back (bottom of z-order)
    back() {
        if (this.element && this.element.parentNode) {
            // Удаляем элемент из DOM
            const parent = this.element.parentNode;
            parent.removeChild(this.element);

            // Добавляем в конец (будет под всеми элементами)
            parent.appendChild(this.element);
        }
        return this;
    }

    // Move element forward by one position in z-order (ближе к переднему плану)
    forward() {
        if (this.element && this.element.parentNode) {
            const parent = this.element.parentNode;
            const previousSibling = this.element.previousSibling;

            // Если есть предыдущий элемент, перемещаем перед ним
            if (previousSibling) {
                parent.removeChild(this.element);
                parent.insertBefore(this.element, previousSibling);
            }
            // Если нет предыдущего элемента, значит мы уже в самом верху
        }
        return this;
    }

    // Move element backward by one position in z-order (ближе к заднему плану)
    backward() {
        if (this.element && this.element.parentNode) {
            const parent = this.element.parentNode;
            const nextSibling = this.element.nextSibling;

            // Если есть следующий элемент, перемещаем после него
            if (nextSibling) {
                parent.removeChild(this.element);

                // Если есть элемент после следующего, вставляем перед ним
                if (nextSibling.nextSibling) {
                    parent.insertBefore(this.element, nextSibling.nextSibling);
                } else {
                    // Иначе добавляем в конец
                    parent.appendChild(this.element);
                }
            }
            // Если нет следующего элемента, значит мы уже в самом низу
        }
        return this;
    }

    // Set fill properties - now accepts partial updates
    fill(options) {
        // Update only the properties that were passed
        if (options.color !== undefined) {
            this._currentFill.color = options.color;
        }
        if (options.opacity !== undefined) {
            this._currentFill.opacity = options.opacity;
        }

        // Apply the current fill settings
        this.element.setAttribute("fill", this._currentFill.color);
        this.element.setAttribute("fill-opacity", this._currentFill.opacity);
        return this;
    }

    // Set stroke properties - now accepts partial updates
    stroke(options) {
        // Update only the properties that were passed
        if (options.color !== undefined) {
            this._currentStroke.color = options.color;
        }
        if (options.width !== undefined) {
            this._currentStroke.width = options.width;
        }
        if (options.opacity !== undefined) {
            this._currentStroke.opacity = options.opacity;
        }

        // Apply the current stroke settings
        this.element.setAttribute("stroke", this._currentStroke.color);
        this.element.setAttribute("stroke-width", this._currentStroke.width);
        this.element.setAttribute(
            "stroke-opacity",
            this._currentStroke.opacity
        );
        return this;
    }

    // Set attribute
    attr(name, value) {
        this.element.setAttribute(name, value);
        return this;
    }

    // Set data attribute
    data(name, value) {
        this.element.setAttribute(`data-${name}`, value);
        return this;
    }

    // Add event listener with SVG.js-like API
    on(eventName, handler) {
        // Проверка входных параметров
        if (!eventName) {
            return this;
        }

        if (!handler || typeof handler !== "function") {
            return this;
        }

        // Проверка, что элемент существует
        if (!this.element) {
            return this;
        }

        // Инициализация массива обработчиков, если его нет
        if (!this._eventListeners[eventName]) {
            this._eventListeners[eventName] = [];
        }

        // Создаем обертку для обработчика, чтобы сохранить контекст
        const wrappedHandler = (event) => {
            handler.call(this, event);
        };

        // Сохраняем ссылку на оригинальный обработчик и обертку
        this._eventListeners[eventName].push({
            original: handler,
            wrapped: wrappedHandler,
        });

        // Добавляем обработчик к элементу
        this.element.addEventListener(eventName, wrappedHandler);

        return this;
    }

    // Remove event listener with SVG.js-like API
    off(eventName, handler) {
        if (!this._eventListeners[eventName]) {
            return this;
        }

        if (handler) {
            // Remove specific handler
            const handlerInfo = this._eventListeners[eventName].find(
                (info) => info.original === handler
            );

            if (handlerInfo) {
                this.element.removeEventListener(
                    eventName,
                    handlerInfo.wrapped
                );
                const index =
                    this._eventListeners[eventName].indexOf(handlerInfo);
                this._eventListeners[eventName].splice(index, 1);
            } else {
                console.warn(
                    `SVGElement.off: handler not found for ${eventName}`
                );
            }
        } else {
            this._eventListeners[eventName].forEach((handlerInfo) => {
                this.element.removeEventListener(
                    eventName,
                    handlerInfo.wrapped
                );
            });
            this._eventListeners[eventName] = [];
        }

        return this;
    }

    // Get bounding box
    bbox() {
        const bbox = this.element.getBBox();
        return {
            x: bbox.x,
            y: bbox.y,
            width: bbox.width,
            height: bbox.height,
        };
    }

    // Remove element
    remove() {
        if (this.element && this.element.parentNode) {
            // Clean up all event listeners
            for (const eventName in this._eventListeners) {
                this.off(eventName);
            }
            this.element.parentNode.removeChild(this.element);
        }
        return this;
    }
}

// Class for polygon manipulation
class SVGPolygon extends SVGElement {
    constructor(svgElement) {
        super(svgElement, "polygon");
        this._points = [];
        this.element.setAttribute("stroke-linejoin", "round");
        return this;
    }

    // Set polygon points
    plot(points) {
        this._points = [...points];
        const pointsString = points.map((point) => point.join(",")).join(" ");
        this.element.setAttribute("points", pointsString);
        return this;
    }

    // Get points array
    array() {
        return {
            valueOf: () => this._points,
        };
    }
}

// Class for circle manipulation
class SVGCircle extends SVGElement {
    constructor(svgElement, diameter) {
        super(svgElement, "circle");
        this.radius(diameter / 2);
        return this;
    }

    // Set radius
    radius(r) {
        this._radius = r;
        this.element.setAttribute("r", r);
        return this;
    }

    // Set center coordinates
    center(x, y) {
        this._cx = x;
        this._cy = y;
        this.element.setAttribute("cx", x);
        this.element.setAttribute("cy", y);
        return this;
    }

    // Get center x coordinate
    cx() {
        return this._cx;
    }

    // Get center y coordinate
    cy() {
        return this._cy;
    }
}
